import { Button } from '@civilgrid/ui/components';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { updateAgencyContact as apiUpdateAgencyContact } from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { useQuery } from '~/common/routerUtils';
import ControlledCheckbox from '~/components2/forms/controlled/ControlledCheckbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import ControlledSelect from '~/components2/forms/controlled/ControlledSelect';
import ControlledTextArea from '~/components2/forms/controlled/ControlledTextArea';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import { statusConfig } from '~/pages/admin/civilsearch/common';
import store from '../../../store';
import { buildSubRegionItems, SUB_REGION_NONE, subRegionFkValue, subRegionFormValue } from '../../../subRegionOptions';

type TFormValues = {
  isDefaultContact: boolean;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  notes: string;
  status: 'active' | 'archived';
  agencySubGeometryId: string;
};

export default function ContactDetails() {
  const query = useQuery();
  const { contacts, agency, subGeometries } = useSnapshot(store) as typeof store;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const contact = useMemo(() => {
    const id = query.get('contactId');
    if (id && contacts[id]) {
      return contacts[id];
    }
  }, [query]);

  const form = useForm<TFormValues>({
    defaultValues: {
      isDefaultContact: agency?.defaultContact?.toString() === contact?.id,
      firstName: contact?.firstName || '',
      lastName: contact?.lastName || '',
      title: contact?.title || '',
      email: contact?.email || '',
      notes: contact?.notes || '',
      status: contact?.status || 'active',
      agencySubGeometryId: subRegionFormValue(contact?.agencySubGeometryId ?? null),
    },
  });

  const statusItems = useMemo(
    () =>
      statusConfig.map(({ value, label }) => ({
        value,
        label: <div className="text-xs">{label}</div>,
      })),
    [],
  );

  const subRegionItems = useMemo(
    () =>
      buildSubRegionItems(subGeometries).map(({ value, label }) => ({
        value,
        label: <div className="text-xs">{label}</div>,
      })),
    [subGeometries],
  );

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    if (!contact?.id) return;

    const properties: { [key: string]: any } = {};

    for (const key in data) {
      const k = key as keyof TFormValues;
      if (form.formState.dirtyFields[k] && data[k] !== undefined) {
        const v = data[k];
        properties[k] = v;
      }
    }

    if (isEmpty(properties)) {
      console.warn('No properties to be updated, invalid submit.');
      return;
    }

    if (properties.agencySubGeometryId === SUB_REGION_NONE) {
      properties.agencySubGeometryId = null;
    }

    try {
      await apiUpdateAgencyContact(contact!.id, properties);

      Notify.info({
        title: 'Update Agency Contact',
        message: 'Agency contact successfully updated',
      });

      if (form.formState.dirtyFields.agencySubGeometryId) {
        const fk = subRegionFkValue(data.agencySubGeometryId);
        capturePosthogEvent(
          fk
            ? POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberAssigned
            : POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberUnassigned,
          {
            agency_id: agency!.id,
            // On unassign `fk` is null; report the sub-region being vacated
            // (from the pre-update snapshot) so the event identifies which
            // sub-region lost the member.
            sub_geometry_id: fk ?? contact?.agencySubGeometryId ?? null,
            member_type: 'contact',
            member_id: contact!.id,
            entry_point: 'contact_editor',
          },
        );
      }

      store.fetchData(agency!.id);
    } catch {
      Notify.error({
        title: 'Update Agency Contact',
        message: 'An error occurred while updating the agency contact',
      });
    }
  };

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <div className="w-1/3 space-y-2">
        <Button type="submit" disabled={!form.formState.isDirty}>
          Save Contact
        </Button>

        <ControlledInput
          label="Agency Contact Email"
          control={form.control}
          name="email"
          isRequired={false}
          tooltipText="Agency contact email address"
          placeholder="Ex: john.smith@acme-engineering.com"
        />
        <ControlledCheckbox label="Agency default contact" control={form.control} name="isDefaultContact" />
        <ControlledInput
          label="Agency Contact First Name"
          control={form.control}
          name="firstName"
          isRequired={false}
          tooltipText="Agency contact first name"
          placeholder="Ex: John"
        />
        <ControlledInput
          label="Agency Contact Last Name"
          control={form.control}
          name="lastName"
          isRequired={false}
          tooltipText="Agency contact last name"
          placeholder="Ex: Smith"
        />
        <ControlledInput
          label="Agency Contact Title"
          control={form.control}
          name="title"
          isRequired={false}
          tooltipText="Agency contact title (i.e. Project Engineer, Manager, etc.)"
          placeholder="Ex: Project Manager"
        />
        <ControlledTextArea
          label="Agency Contact Notes"
          control={form.control}
          name="notes"
          isRequired={false}
          tooltipText="Notes for the agency contact"
          placeholder="Add notes here for this agency contact."
        />
        <ControlledSelect
          label="Sub Region"
          control={form.control}
          items={subRegionItems}
          name="agencySubGeometryId"
          isRequired
          tooltipText="Assign this contact to one of the agency's sub regions, or leave unscoped"
          data-testid="agency-sub-geometries-contact-sub-region-select"
        />
        <ControlledSelect
          label="Contact Status"
          control={form.control}
          items={statusItems}
          name="status"
          isRequired
          tooltipText="Mark agency contact as active or archived"
        />
      </div>
    </Form>
  );
}
